import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import ScheduleRow, { DayColumn } from './ScheduleRow';
import useScheduleStore, { DayBar, BlockBar } from '../stores/useScheduleStore';
import { COL_GLOW_CLASSES, PENDING_EVENT_COLOR } from './schedule-colors';
import { CrewAssignment, ScheduleEvent } from './types';
import { enumerateDays } from '../utils/date-utils';
import { isWeekend, dayOfMonth, dayOfWeek, todayYmd, monthLabel } from './day-utils';
import { drivesOut } from '../components/TransportChips';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Inline SVG transport icons — visually distinct shipping box vs. truck */
const ShippingIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={className}>
    <rect x="2" y="3" width="12" height="10" rx="1" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M2 6H14" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M6 6V13" stroke="currentColor" strokeWidth="0.8" opacity="0.4"/>
    <path d="M10 6V13" stroke="currentColor" strokeWidth="0.8" opacity="0.4"/>
    <path d="M5 3V6" stroke="currentColor" strokeWidth="0.8" opacity="0.4"/>
    <path d="M8 1V3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    <path d="M6.5 1.5L8 0.5L9.5 1.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TruckIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={className}>
    {/* Cab */}
    <path d="M8 5H12.5L14.5 8V11H8V5Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
    {/* Cab window */}
    <rect x="9.5" y="6" width="2" height="2" rx="0.3" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
    {/* Open bed */}
    <path d="M1.5 7.5H8V11H1.5V7.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
    {/* Tailgate */}
    <path d="M1.5 7.5V11" stroke="currentColor" strokeWidth="1.4"/>
    {/* Wheels */}
    <circle cx="4" cy="12.5" r="1.5" stroke="currentColor" strokeWidth="1.2"/>
    <circle cx="12" cy="12.5" r="1.5" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
);

interface ScheduleGridProps {
  onCellClick: (userId: string, dateStr: string, rect: DOMRect, eventId?: string) => void;
  collapsedEmpty: boolean;
  eventColorMap: Map<string, import('./types').EventColorEntry>;
  eventLookup: Map<string, ScheduleEvent>;
  eventsPerDay: Map<string, ScheduleEvent[]>;
  oooByUserDay: Map<string, Map<string, import('./types').OOOEntry[]>>;
  effectiveDayBars: Map<string, Map<string, DayBar[]>>;
  /** Per-user effective block bars (passed empty when the showBlocks filter is off). */
  effectiveBlockBars: Map<string, Map<string, BlockBar>>;
  /** Increments when the header's Today button is pressed. Optional: the grid
   *  still auto-scrolls to today on mount without one. */
  jumpToken?: number;
  /** Crew view (items 7+14). Disables every write path and blanks the producer
   *  workload column, which the crew payload deliberately does not carry. */
  readOnly?: boolean;
}

// Module-level constants to avoid creating new empty refs on every render
// Read-only stand-ins. Module-level so they are referentially stable and the
// row memo does not thrash. Belt-and-braces with ScheduleCell's own readOnly
// guard: two independent reasons a crew click can never write.
const NOOP_CLICK = () => {};
const NOOP_DRAG = () => {};
const NOOP_DROP = () => {};
const NOOP_OVER = (e: React.DragEvent) => { e.preventDefault(); };

const EMPTY_MAP = new Map();
const EMPTY_BAR_MAP: Map<string, DayBar[]> = new Map();
const EMPTY_BLOCK_MAP: Map<string, BlockBar> = new Map();

const ScheduleGrid: React.FC<ScheduleGridProps> = ({
  onCellClick, collapsedEmpty, eventColorMap, eventLookup, eventsPerDay, oooByUserDay, effectiveDayBars, effectiveBlockBars,
  jumpToken = 0,
  readOnly = false,
}) => {
  const {
    days, events, staff,
    pendingChanges, showCounts, addPendingChange,
  } = useScheduleStore();

  // Cross-hair hover: which staff row and which day column is currently hovered.
  // Both are nulled out on mouseleave. Stable callback so memoized rows/cells don't re-render
  // on every hover change — only the ones whose isRowHovered/isColHovered flag flipped.
  const [hoveredUserId, setHoveredUserId] = useState<string | null>(null);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const handleCellHover = useCallback((userId: string | null, date: string | null) => {
    setHoveredUserId(userId);
    setHoveredDate(date);
  }, []);

  // A date string, so it needs no month context to be comparable. Computed once:
  // re-reading the clock per render would make every cell's memo compare unequal.
  const today = useMemo(() => todayYmd(), []);

  // Which days have events. Today is always kept so the window has an anchor to
  // scroll to even in a stretch with no shows.
  const activeDays = useMemo(() => {
    const active = new Set<string>();
    for (const ds of days) if (eventsPerDay.has(ds)) active.add(ds);
    active.add(today);
    return active;
  }, [eventsPerDay, days, today]);

  /**
   * Build the dayColumns array — the single source of truth for grid columns.
   * When collapsed: runs of 3+ consecutive empty days become a single
   * {type:'collapse'} column. First day of each run is always shown.
   * When not collapsed: one column per day in the window.
   * The grid template is derived from dayColumns.length — guaranteed to match.
   *
   * Iterates `days` rather than 1..daysInMonth, which is what lets the window
   * cross a month boundary. A collapse run may swallow a month start, in which
   * case that month's separator label is absent — the run indicator already
   * marks the gap, so it reads correctly.
   */
  const dayColumns = useMemo((): DayColumn[] => {
    if (!collapsedEmpty) {
      return days.map((date) => ({ type: 'day' as const, date }));
    }

    const cols: DayColumn[] = [];
    let i = 0;
    while (i < days.length) {
      if (activeDays.has(days[i])) {
        cols.push({ type: 'day', date: days[i] });
        i++;
        continue;
      }
      // Start of an empty run — find its length
      const runStart = i;
      while (i < days.length && !activeDays.has(days[i])) i++;
      const runLen = i - runStart;

      if (runLen >= 3) {
        // Show first day of run, then a collapse indicator for the rest
        cols.push({ type: 'day', date: days[runStart] });
        cols.push({ type: 'collapse', count: runLen - 1 });
      } else {
        for (let j = runStart; j < runStart + runLen; j++) {
          cols.push({ type: 'day', date: days[j] });
        }
      }
    }

    return cols;
  }, [collapsedEmpty, activeDays, days]);

  // Sticky month labels replace the ◀▶ pager: with a continuous window the user
  // needs to know where they are, and the day numbers alone restart every month.
  // Year is appended only when the window crosses New Year, so "Jan" is never
  // ambiguous but the common case stays terse.
  const monthHeaders = useMemo(() => {
    // Walk dayColumns, not `days`: the grid template has one track per entry
    // whatever its type, so a collapse column has to count toward the span of the
    // month it sits in or every label after it drifts left.
    const runs: Array<{ key: string; span: number }> = [];
    let lastMonth: string | null = null;
    for (const col of dayColumns) {
      if (col.type === 'day') {
        const ym = col.date.slice(0, 7);
        if (ym !== lastMonth) {
          runs.push({ key: ym, span: 0 });
          lastMonth = ym;
        }
      }
      if (runs.length) runs[runs.length - 1].span++;
    }
    const spansNewYear = new Set(runs.map(r => r.key.slice(0, 4))).size > 1;
    return runs.map(r => ({ ...r, label: monthLabel(`${r.key}-01`, spansNewYear) }));
  }, [dayColumns]);

  // Staff grouping: Pilots | Producer | Primary | Backup
  const { pilotStaff, primaryStaff, producerStaff, backupStaff } = useMemo(() => {
    const pilots: typeof staff = [];
    const primary: typeof staff = [];
    const producers: typeof staff = [];
    const backup: typeof staff = [];

    for (const s of staff) {
      if (s.can_producer) {
        producers.push(s);
      } else if (s.can_pilot && s.travel_tier !== 'backup') {
        pilots.push(s);
      } else if (s.travel_tier === 'backup') {
        backup.push(s);
      } else {
        primary.push(s);
      }
    }
    return { pilotStaff: pilots, primaryStaff: primary, producerStaff: producers, backupStaff: backup };
  }, [staff]);

  // Drag-and-drop handlers
  const handleDragStart = useCallback(
    (e: React.DragEvent, assignment: CrewAssignment, userId: string, shiftRange?: { start: string; end: string }) => {
      e.dataTransfer.setData(
        'application/json',
        JSON.stringify({
          event_id: assignment.event_id,
          user_id: userId,
          role: assignment.role,
          shiftStart: shiftRange?.start,
          shiftEnd: shiftRange?.end,
        }),
      );
      e.dataTransfer.effectAllowed = 'move';
    },
    [],
  );

  const handleDrop = useCallback((e: React.DragEvent, targetUserId: string, _targetDateStr: string) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('application/json');
    if (!data) return;

    const source = JSON.parse(data) as {
      event_id: string;
      user_id: string;
      role: string;
      shiftStart?: string;
      shiftEnd?: string;
    };
    if (source.user_id === targetUserId) return; // Same person — no-op

    const sourceEvent = eventLookup.get(source.event_id);
    if (!sourceEvent) return;

    const sourceEventName = sourceEvent.name;
    const targetStaff = staff.find(s => s.id === targetUserId);
    const sourceStaff = staff.find(s => s.id === source.user_id);
    const userNameShort = (s?: typeof targetStaff) => (s ? `${s.firstname} ${s.lastname}` : '');

    // Move the shift, preserving its date range. If shiftStart/End weren't
    // provided (legacy callers — none currently after Phase 3), fall back to
    // a dateless whole-event move so behavior at least matches the old code.
    if (source.shiftStart && source.shiftEnd) {
      const days = enumerateDays(source.shiftStart, source.shiftEnd);
      // Random suffix, not the bare timestamp: two drops inside one millisecond
      // would otherwise share both the moveId AND the per-item ids, and the
      // save clears committed items by id, so a collision drops the wrong ones.
      // Matches the legacy dateless path's idiom a few lines down.
      const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      // Both halves carry the same moveId. The save reassembles them into one
      // atomic /schedule/move: sent as a loose remove and add, a swap on a day
      // OUTSIDE the event span (a travel or load-in day, which is legal) had
      // its add refused as "outside event range" while the remove had already
      // committed, so the person was simply deleted off the day.
      const moveId = `move-${stamp}`;
      days.forEach((day, i) => {
        addPendingChange({
          id: `move-rm-${stamp}-${i}`,
          moveId,
          action: 'remove',
          event_id: source.event_id,
          user_id: source.user_id,
          date: day,
          event_name: sourceEventName,
          user_name: userNameShort(sourceStaff),
        });
        addPendingChange({
          id: `move-add-${stamp}-${i}`,
          moveId,
          action: 'add',
          event_id: source.event_id,
          user_id: targetUserId,
          role: source.role,
          date: day,
          event_name: sourceEventName,
          user_name: userNameShort(targetStaff),
        });
      });
      return;
    }

    // Legacy dateless path
    addPendingChange({
      id: `rm-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      action: 'remove',
      event_id: source.event_id,
      user_id: source.user_id,
      event_name: sourceEventName,
      user_name: userNameShort(sourceStaff),
    });
    addPendingChange({
      id: `add-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      action: 'add',
      event_id: source.event_id,
      user_id: targetUserId,
      role: source.role,
      event_name: sourceEventName,
      user_name: userNameShort(targetStaff),
    });
  }, [eventLookup, staff, addPendingChange]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  // Grid template derived directly from dayColumns.length — guarantees alignment
  const gridCols = `160px repeat(${dayColumns.length}, minmax(36px, 1fr)) 60px`;

  /**
   * Scroll today into view. The window opens 14 days in the past, so without this
   * the producer lands on a fortnight of history every time — which would be the
   * "I keep swiping" complaint again, wearing a different hat.
   *
   * Auto-fires once per mount, then only when jumpToken changes. Re-firing on
   * every data change would yank her back from wherever she scrolled after each
   * save, which is the same class of bug the `quiet` refetch flag exists to avoid.
   */
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasAutoScrolled = useRef(false);
  // The jumpToken we last acted on. Comparing against 0 instead — as this did
  // first — means the bail-out dies permanently after the first Today press, so
  // any later change to the column set (toggling Collapse empty, say) re-scrolls
  // and yanks the producer back from wherever she was reading.
  const lastJump = useRef(0);
  const todayCol = useMemo(
    () => dayColumns.findIndex(c => c.type === 'day' && c.date >= today),
    [dayColumns, today],
  );
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || todayCol < 0) return;
    const firstTime = !hasAutoScrolled.current;
    const jumped = jumpToken !== lastJump.current;
    if (!firstTime && !jumped) return;
    hasAutoScrolled.current = true;
    lastJump.current = jumpToken;

    // Read today's real offset rather than multiplying an average column width.
    // Tracks are minmax(36px, 1fr) AND the day-number header holds event chips, so
    // a day with three shows is wider than an empty one and an average drifts.
    const cell = el.querySelector<HTMLElement>(`[data-date="${today}"]`);
    if (cell) {
      // Three columns of lead-in so today is not flush against the frozen names.
      el.scrollTo({
        left: Math.max(0, cell.offsetLeft - 160 - cell.offsetWidth * 3),
        behavior: firstTime ? 'auto' : 'smooth',
      });
      return;
    }
    // Fallback: today is outside the window (or collapsed away), so approximate.
    const trackWidth = (el.scrollWidth - 220) / Math.max(1, dayColumns.length);
    el.scrollTo({
      left: Math.max(0, todayCol * trackWidth - trackWidth * 3),
      behavior: firstTime ? 'auto' : 'smooth',
    });
  }, [todayCol, jumpToken, dayColumns.length, today]);

  const renderSection = (
    sectionStaff: typeof staff,
    sectionLabel: string,
    collapsible?: boolean,
    expanded?: boolean,
    onToggle?: () => void,
  ) => {
    if (!sectionStaff.length) return null;

    return (
      <>
        {/* Section header */}
        <div
          className={`col-span-full sticky left-0 z-20 bg-gray-850 day:bg-gray-100 border-b border-gray-700/60 day:border-gray-200
            px-3 py-1.5 text-xs font-semibold text-on-surface-variant day:text-gray-700 uppercase tracking-wider
            ${collapsible ? 'cursor-pointer hover:text-on-surface day:hover:text-gray-900' : ''}`}
          onClick={onToggle}
        >
          {collapsible && (
            <span className="mr-1.5 text-on-surface-variant day:text-gray-500">
              {expanded ? '▾' : '▸'}
            </span>
          )}
          {sectionLabel}
          <span className="ml-2 text-on-surface-variant day:text-gray-500 font-normal normal-case">
            ({sectionStaff.length})
          </span>
        </div>

        {/* Staff rows */}
        {(!collapsible || expanded) && sectionStaff.map((s) => (
          <ScheduleRow
            key={s.id}
            staff={s}
            dayColumns={dayColumns}
            barsByDay={effectiveDayBars.get(s.id) || EMPTY_BAR_MAP}
            blocksByDay={effectiveBlockBars.get(s.id) || EMPTY_BLOCK_MAP}
            eventsPerDay={eventsPerDay}
            oooPerDay={oooByUserDay.get(s.id) || EMPTY_MAP}
            eventColorMap={eventColorMap}
            eventLookup={eventLookup}
            today={today}
            readOnly={readOnly}
            monthlyCount={showCounts.monthly[s.id] || 0}
            trailingCount={showCounts.trailing[s.id] || 0}
            isRowHovered={hoveredUserId === s.id}
            hoveredDate={hoveredDate}
            onCellHover={handleCellHover}
            onCellClick={readOnly ? NOOP_CLICK : onCellClick}
            onDragStart={readOnly ? NOOP_DRAG : handleDragStart}
            onDrop={readOnly ? NOOP_DROP : handleDrop}
            onDragOver={readOnly ? NOOP_OVER : handleDragOver}
          />
        ))}
      </>
    );
  };

  // Drone counts per day, keyed BY DATE. This was an array indexed `col.day - 1`,
  // which silently reads the wrong day the moment the window is not one month.
  const droneCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ds of days) {
      const dayEvents = eventsPerDay.get(ds);
      if (!dayEvents?.length) continue;
      counts.set(ds, dayEvents.reduce((sum, evt) => sum + Math.ceil(
        (evt.ls_drone_count + evt.pyro_drone_count) * (1 + evt.buffer_percentage / 100)
      ), 0));
    }
    return counts;
  }, [eventsPerDay, days]);

  return (
    <div ref={scrollRef} className="overflow-x-auto border border-gray-700/60 day:border-gray-200 rounded-lg bg-gray-900 day:bg-white">
      <div
        className="grid min-w-max"
        style={{ gridTemplateColumns: gridCols }}
      >
        {/* ── Header row: month bands ──
            New for the continuous window. The ◀▶ pager used to answer "which
            month am I looking at"; nothing else does, since day numbers restart. */}
        <div className="sticky left-0 z-30 bg-gray-850 day:bg-gray-50 border-b border-r border-gray-700/60 day:border-gray-200 px-2 py-1
          text-[10px] text-on-surface-variant day:text-gray-600 font-medium">
          Month
        </div>
        {monthHeaders.map((m) => (
          <div
            key={`month-${m.key}`}
            style={{ gridColumn: `span ${m.span}` }}
            className="border-b border-r border-gray-700/60 day:border-gray-200 py-0.5 px-1
              bg-gray-850/60 day:bg-gray-50"
          >
            {/* Sticks to the left edge of the scroll port so the label stays
                visible while its own month is still on screen. */}
            <span className="sticky left-[164px] text-[11px] font-semibold text-on-surface day:text-gray-800 whitespace-nowrap">
              {m.label}
            </span>
          </div>
        ))}
        <div className="sticky right-0 z-30 bg-gray-850 day:bg-gray-50 border-b border-l border-gray-700/60 day:border-gray-200" />

        {/* ── Header row: day-of-week ── */}
        <div className="sticky left-0 z-20 bg-gray-850 day:bg-gray-50 border-b border-r border-gray-700/60 day:border-gray-200 px-2 py-1" />
        {dayColumns.map((col, i) => {
          if (col.type === 'collapse') {
            return (
              <div key={`collapse-dow-${i}`}
                className="text-center text-[9px] text-on-surface-variant day:text-gray-500 border-b border-r border-gray-800/60 day:border-gray-200 py-0.5
                  bg-gray-800/20 day:bg-gray-100 flex items-center justify-center">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-40">
                  <path d="M3 4L6 7L9 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
              </div>
            );
          }
          const ds = col.date;
          return (
            <div
              key={`dow-${ds}`}
              className={`text-center text-[10px] font-medium border-b border-r border-gray-800/60 day:border-gray-200 py-0.5
                ${isWeekend(ds) ? 'text-on-surface-variant day:text-gray-500 bg-gray-800/40 day:bg-gray-100' : 'text-on-surface-variant day:text-gray-700'}
                ${today === ds ? 'bg-primary/5 day:bg-amber-50 text-primary-ink day:text-amber-700' : ''}
                ${hoveredDate === ds ? COL_GLOW_CLASSES : ''}`}
            >
              {DAY_NAMES[dayOfWeek(ds)]}
            </div>
          );
        })}
        <div className="sticky right-0 z-20 bg-gray-850 day:bg-gray-50 border-b border-l border-gray-700/60 day:border-gray-200 px-1 py-1
          text-[10px] text-on-surface-variant day:text-gray-600 text-center font-medium">
          Shows
        </div>

        {/* ── Header row: day numbers + events summary ── */}
        <div className="sticky left-0 z-20 bg-gray-850 day:bg-gray-50 border-b border-r border-gray-700/60 day:border-gray-200 px-2 py-1
          text-xs text-on-surface-variant day:text-gray-700 font-medium">
          Events on Day
        </div>
        {dayColumns.map((col, i) => {
          if (col.type === 'collapse') {
            return (
              <div key={`collapse-day-${i}`}
                className="border-b border-r border-gray-800/60 day:border-gray-200 bg-gray-800/20 day:bg-gray-100 flex items-center justify-center">
                <span className="text-[9px] text-primary day:text-amber-700 font-medium">{col.count}d</span>
              </div>
            );
          }
          const ds = col.date;
          const dayEvents = eventsPerDay.get(ds) || [];
          return (
            <div
              key={`day-${ds}`}
              className={`text-center border-b border-r border-gray-800/60 day:border-gray-200 py-0.5
                flex flex-col items-stretch justify-center
                ${isWeekend(ds) ? 'bg-gray-800/40 day:bg-gray-100' : ''}
                ${today === ds ? 'bg-primary/5 day:bg-amber-50' : ''}
                ${hoveredDate === ds ? COL_GLOW_CLASSES : ''}`}
            >
              <div className={`text-[11px] font-semibold mb-0.5
                ${today === ds ? 'text-primary-ink day:text-amber-700' : 'text-on-surface day:text-gray-800'}`}>
                {dayOfMonth(ds)}
              </div>
              {dayEvents.map(evt => {
                const color = evt.status === 'pending' ? PENDING_EVENT_COLOR : eventColorMap.get(evt.id);
                const transportTitle = evt.details_json?.gear_transportation
                  || `${evt.name}: ${drivesOut(!!evt.has_vehicle, evt.transport_mode) ? 'driving' : (evt.transport_mode || 'tbd')}`;
                return (
                  <div
                    key={evt.id}
                    className={`rounded px-0.5 text-[8px] font-medium truncate mx-0.5 mb-0.5 flex items-center gap-0.5
                      ${color?.bg || 'bg-surface-container-highest day:bg-gray-200'} ${color?.text || 'text-on-surface day:text-gray-700'}`}
                    title={`${evt.name}${evt.status === 'pending' ? ' (pending)' : ''} — ${evt.ls_drone_count + evt.pyro_drone_count} drones\n${transportTitle}`}
                  >
                    {/* A truck on the show is a drive whatever the mode says;
                        shipping only when nothing of ours is going. */}
                    {drivesOut(!!evt.has_vehicle, evt.transport_mode)
                      ? <TruckIcon className="w-3 h-3 shrink-0 opacity-60" />
                      : evt.transport_mode === 'shipping'
                      ? <ShippingIcon className="w-3 h-3 shrink-0 opacity-60" />
                      : null}
                    <span className="truncate">{evt.name.length > 10 ? evt.name.slice(0, 9) + '…' : evt.name}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
        <div className="sticky right-0 z-20 bg-gray-850 day:bg-gray-50 border-b border-l border-gray-700/60 day:border-gray-200 px-1 py-1
          text-[10px] text-on-surface-variant day:text-gray-600 text-center">
          Mo/90d
        </div>

        {/* ── Drone count row ── */}
        <div className="sticky left-0 z-20 bg-gray-850 day:bg-gray-50 border-b border-r border-gray-700/60 day:border-gray-200 px-2 py-1
          text-xs text-on-surface-variant day:text-gray-700 font-medium">
          Drone Count
        </div>
        {dayColumns.map((col, i) => {
          if (col.type === 'collapse') {
            return <div key={`collapse-drones-${i}`} className="border-b border-r border-gray-800/60 day:border-gray-200 bg-gray-800/20 day:bg-gray-100" />;
          }
          const count = droneCounts.get(col.date) || 0;
          return (
            <div
              key={`drones-${col.date}`}
              className={`border-b border-r border-gray-800/60 day:border-gray-200 px-0.5 py-1 text-center text-[10px] font-mono
                ${hoveredDate === col.date ? COL_GLOW_CLASSES : ''}`}
            >
              {count > 0 && (
                <span className={count >= 500 ? 'text-primary day:text-amber-700 font-semibold' : 'text-on-surface-variant day:text-gray-700'}>
                  {count}
                </span>
              )}
            </div>
          );
        })}
        <div className="sticky right-0 z-20 bg-gray-850 day:bg-gray-50 border-b border-l border-gray-700/60 day:border-gray-200" />

        {/* ── Staff sections ── */}
        {renderSection(pilotStaff, 'Pilots')}
        {renderSection(producerStaff, 'Producer')}
        {renderSection(primaryStaff, 'Primary')}
        {renderSection(backupStaff, 'Backup')}

      </div>
    </div>
  );
};

export default ScheduleGrid;
